/**
 * @file Forgot Password Page — /forgot-password
 */
import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { ArrowLeft, AlertCircle, CheckCircle2 } from "lucide-react";
import { authService } from "../services/auth";
import { AUTH_STRINGS as S } from "../constants/strings";
import AuthShell from "../components/AuthShell";
import { Button, Input } from "../ui";

const schema = Yup.object({
  email: Yup.string().email(S.INVALID_EMAIL).required(S.REQUIRED_FIELD),
});

export default function ForgotPassword() {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError("");
      await authService.forgotPassword(values.email);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || "Error al procesar la solicitud");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <AuthShell
        title={S.FORGOT_TITLE}
        footer={
          <RouterLink to="/login/profesional" className="auth-back">
            <ArrowLeft size={12} /> {S.FORGOT_BACK}
          </RouterLink>
        }
      >
        <div className="auth-alert auth-alert-ok">
          <CheckCircle2 size={14} />
          <span>Si el correo existe, recibirás instrucciones para restablecer tu contraseña.</span>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={S.FORGOT_TITLE}
      subtitle={S.FORGOT_SUBTITLE}
      footer={
        <RouterLink to="/login" className="auth-back">
          <ArrowLeft size={12} /> {S.FORGOT_BACK}
        </RouterLink>
      }
    >
      {error && (
        <div className="auth-alert auth-alert-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
      <Formik initialValues={{ email: "" }} validationSchema={schema} onSubmit={handleSubmit}>
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
          <Form noValidate className="auth-form">
            <Input
              name="email"
              type="email"
              label={S.FORGOT_EMAIL}
              autoComplete="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && errors.email}
            />
            <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
              {S.FORGOT_SUBMIT}
            </Button>
          </Form>
        )}
      </Formik>
    </AuthShell>
  );
}
